import React from 'react';
import type { EventListItem } from '../types';
import './EventItem.css';

interface EventItemProps {
  event: EventListItem;
}

/**
 * EventItem component renders individual EC2 event details
 * Requirements: 4.4
 */
export const EventItem: React.FC<EventItemProps> = ({ event }) => {
  const { message } = event;

  // Determine event type category for color coding
  const getEventTypeClass = (eventType: string): string => {
    if (eventType.includes('Launch Successful') || eventType.includes('running')) {
      return 'event-type-success';
    }
    if (eventType.includes('Terminate') || eventType.includes('terminated')) {
      return 'event-type-danger';
    }
    if (eventType.includes('Unsuccessful') || eventType.includes('failed')) {
      return 'event-type-error';
    }
    if (eventType.includes('stopping') || eventType.includes('stopped')) {
      return 'event-type-warning';
    }
    return 'event-type-info';
  };

  // Determine state badge color
  const getStateClass = (state: string): string => {
    switch (state.toLowerCase()) {
      case 'running':
        return 'state-running';
      case 'stopped':
      case 'stopping':
        return 'state-stopped';
      case 'terminated':
      case 'terminating':
        return 'state-terminated';
      case 'pending':
        return 'state-pending';
      default:
        return 'state-default';
    }
  };

  // Format timestamp for readability
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);

    // Show relative time for recent events
    if (diffSecs < 60) {
      return `${diffSecs}s ago`;
    }
    if (diffMins < 60) {
      return `${diffMins}m ago`;
    }
    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }

    // Show formatted date/time for older events
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const eventTypeClass = getEventTypeClass(message.eventType);
  const stateClass = getStateClass(message.state);

  return (
    <div className="event-item">
      <div className="event-item-header">
        <span className={`event-item-type ${eventTypeClass}`}>
          {message.eventType}
        </span>
        <span className="event-item-timestamp">
          {formatTimestamp(message.timestamp)}
        </span>
      </div>

      <div className="event-item-body">
        <div className="event-item-primary">
          <span className="event-item-instance-id">
            {message.instanceId}
          </span>
          <span className={`event-item-state ${stateClass}`}>
            {message.state}
          </span>
        </div>

        <div className="event-item-metadata">
          {message.region && (
            <span className="event-item-region">
              <span className="event-item-label">Region:</span> {message.region}
            </span>
          )}
          {message.availabilityZone && (
            <span className="event-item-az">
              <span className="event-item-label">AZ:</span> {message.availabilityZone}
            </span>
          )}
        </div>

        {message.details && Object.keys(message.details).length > 0 && (
          <div className="event-item-details">
            <span className="event-item-label">Details:</span>
            <div className="event-item-details-content">
              {Object.entries(message.details).map(([key, value]) => (
                <span key={key} className="event-item-detail">
                  <strong>{key}:</strong> {JSON.stringify(value)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
